import { Button, Checkbox, Grid, Input, Link, Select, Typography, Modal, Message } from "@arco-design/web-react";
import { IconDelete } from "@arco-design/web-react/icon";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";


import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import SubscriptionSelector from "../../Components/Subscriptions/Subscriptions";
import NewContactListDialog from "../../ContactLists/NewContactListDialog";
import { uuidOrNull } from "../../utils/validators";
import { confirmIfLive } from "../../utils/confirmIfLive";
import IncentiveEditor from "./IncentiveEditor";
import AfterConfirmQuestion from "./AfterConfirmQuestion";
import { afterConfirmChoice, afterConfirmSettings } from "../afterConfirm";
import { useUnsavedGuard } from "../../utils/useUnsavedGuard";
import { FetchApi, DeleteApi } from "../../Api/AuthenticatedApi";

// Always-editable form (no Edit-mode gate): fields are live, Save enables only
// when something changed vs. the loaded record, and a Discard appears while
// dirty. Because the campaign can be live and taking traffic, Save routes
// through confirmIfLive() so a live campaign asks before committing.

const { Text } = Typography;
// NB: Arco's Link is a top-level export, not Typography.Link (which is undefined).
const { Row, Col } = Grid;
const Option = Select.Option;

// Indented under the "Give signups a reward" checkbox that opens it.
const IncentivePanel = styled.div`
  margin: 8px 0 12px 24px;
  padding: 12px 14px;
  border-left: 3px solid rgb(var(--arcoblue-5));
  background: var(--color-fill-1);
  border-radius: 0 6px 6px 0;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-bottom: 4px;
`;

const Field = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const DELETE_BUTTON_TEXT = "Delete";
// The free-form Campaign Type isn't used anywhere yet, so it's hidden.
const SHOW_CAMPAIGN_TYPE = false;
const SAVE_BUTTON_TEXT = "Save";

// The canonical form values as derived from a loaded campaign record. Mirrors the
// coercions in setFromRecord() exactly so a freshly-loaded form reads as clean.
function valuesFromRecord(r) {
    if (!r) {
        return { name: "", type: "", thankYouUrl: "", confirmedUrl: "", doubleOptIn: false, includeIncentive: false, incentiveDelivery: "redirect", senderEmailId: "", productId: "", contactListIds: [] };
    }
    // What happens after Confirm, as the form shows (and would save) it: their
    // page's address only counts where the choice uses it.
    const choice = afterConfirmChoice(!!r.include_incentive, r.incentive_delivery, r.confirm_email_url);
    const after = afterConfirmSettings(choice, !!r.include_incentive, r.incentive_delivery || "redirect", r.confirm_email_url);
    return {
        name: "" + r.name,
        type: "" + r.type,
        thankYouUrl: "" + r.form_thank_you_url,
        confirmedUrl: after.confirmedUrl,
        doubleOptIn: !!r.double_opt_in,
        includeIncentive: !!r.include_incentive,
        incentiveDelivery: after.incentiveDelivery,
        senderEmailId: "" + r.sender_email_id,
        productId: r.product_id || "",
        contactListIds: r.contact_list_ids || [],
    };
}

function sameIds(a, b) {
    const sa = [...(a || [])].map(String).sort();
    const sb = [...(b || [])].map(String).sort();
    return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

function sameValues(a, b) {
    return (
        a.name === b.name &&
        a.type === b.type &&
        a.thankYouUrl === b.thankYouUrl &&
        a.confirmedUrl === b.confirmedUrl &&
        a.doubleOptIn === b.doubleOptIn &&
        a.includeIncentive === b.includeIncentive &&
        a.incentiveDelivery === b.incentiveDelivery &&
        a.senderEmailId === b.senderEmailId &&
        a.productId === b.productId &&
        sameIds(a.contactListIds, b.contactListIds)
    );
}


export default function CampaignSettingsEditor({
    classes,
    doubleOptIn, setDoubleOptIn,
    includeIncentive, setIncludeIncentive,
    allContactListItems,
    setAllContactListItems,
    confirmedSenderEmails,
    record,
    saveCampaign,
    // The campaign page draws the flow from these as they change (saved or
    // not), and asks to save before testing: onFlowChange(values) and
    // controlRef.current = { isDirty, save() -> Promise<saved?>, discard() }.
    onFlowChange,
    onIncentive,
    onGoToEmails,
    controlRef,
}) {
    const [senderEmailId, setSenderEmailId] = useState("");

    const [campaignName, setCampaignName] = useState("");
    const [campaignType, setCampaignType] = useState("");
    const [thankYouUrl, setThankYouUrl] = useState("");
    // What happens after the confirm click: one choice (afterConfirm.js) saved
    // as the reward's delivery and their page's address (confirmedUrl, typed
    // here; saved only when the choice uses it).
    const [confirmedUrl, setConfirmedUrl] = useState("");
    const [afterConfirm, setAfterConfirm] = useState("confirmed");
    const [urlMissing, setUrlMissing] = useState(false);
    const [selectedContactListIds, setSelectedContactListIds] = useState([]);
    const [selectedContactLists, setSelectedContactLists] = useState([]);
    const [addListOpen, setAddListOpen] = useState(false);
    const [productId, setProductId] = useState("");
    const [incentiveDelivery, setIncentiveDelivery] = useState("redirect");
    const [products, setProducts] = useState([]);

    const { getAccessTokenSilently } = useAuth0();
    useEffect(() => {
        FetchApi(getAccessTokenSilently, "/api/products/",
            (data) => setProducts(data || []), () => {}, () => {});
    }, [getAccessTokenSilently]);


    const setFromRecord = (r) => {
        if (!r) {
            return
        }
        setCampaignName("" + r.name)
        setCampaignType("" + r.type)
        setThankYouUrl("" + r.form_thank_you_url)
        setConfirmedUrl(r.confirm_email_url || "")
        setDoubleOptIn(!!r.double_opt_in)
        setIncludeIncentive(!!r.include_incentive)
        setSenderEmailId("" + r.sender_email_id);
        setProductId(r.product_id || "");
        setIncentiveDelivery(r.incentive_delivery || "redirect");
        setAfterConfirm(afterConfirmChoice(!!r.include_incentive, r.incentive_delivery, r.confirm_email_url));
        setUrlMissing(false);
        setSelectedContactListIds(r.contact_list_ids || []);
    }

    useEffect(() => {
        setFromRecord(record);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    useEffect(() => {
        // mapper between ids and full items
        const selected = (selectedContactListIds && allContactListItems) ?
            allContactListItems.filter(obj => selectedContactListIds.includes(obj.id)) : [];
        setSelectedContactLists(selected);
    }, [allContactListItems, selectedContactListIds])

    let navigate = useNavigate();

    // Dirty tracking: compare the live form against the loaded record. The record
    // prop updates on save success (parent calls setRecord), so this resets to
    // clean automatically once a save lands.
    const baseline = useMemo(() => valuesFromRecord(record), [record]);
    const after = afterConfirmSettings(afterConfirm, includeIncentive, incentiveDelivery, confirmedUrl);
    const current = {
        name: campaignName,
        type: campaignType,
        thankYouUrl,
        confirmedUrl: after.confirmedUrl,
        doubleOptIn,
        includeIncentive,
        incentiveDelivery: after.incentiveDelivery,
        senderEmailId,
        productId,
        contactListIds: selectedContactListIds,
    };
    // The reward's limits save with the settings too (IncentiveEditor).
    const limitsRef = React.useRef(null);
    const [limitsDirty, setLimitsDirty] = useState(false);
    const settingsDirty = !sameValues(current, baseline);
    const isDirty = settingsDirty || (includeIncentive && limitsDirty);
    const hasSender = (confirmedSenderEmails || []).some((s) => s.id === senderEmailId);
    useUnsavedGuard(isDirty);

    // "Live" = the campaign is getting traffic. Editing is always allowed; this
    // only gates the Save so a live campaign confirms before committing.
    const views = (record && record.views) || 0;
    const conversions = (record && record.conversions) || 0;
    const isLive = views > 0 || conversions > 0;
    const liveReason =
        `“${(record && record.name) || "This campaign"}” has received ` +
        `${views.toLocaleString()} view${views === 1 ? "" : "s"} and ` +
        `${conversions.toLocaleString()} conversion${conversions === 1 ? "" : "s"}. ` +
        `Saving applies your changes to the version people are seeing.`;

    // Turning the reward on or off changes the choices; keep what it amounts to.
    const toggleIncentive = (checked) => {
        setIncludeIncentive(checked);
        setAfterConfirm(afterConfirmChoice(checked, after.incentiveDelivery, after.confirmedUrl));
    };

    // save resolves true once saved (false: not saved, e.g. cancelled).
    const save = async () => {
        if (doubleOptIn && after.usesUrl && !after.confirmedUrl) {
            setUrlMissing(true);
            Message.error("Enter your page's address (under “After they click Confirm”)");
            return false;
        }
        const ok = await confirmIfLive({
            live: isLive,
            title: "This campaign is live",
            content: liveReason,
            okText: "Save changes",
        });
        if (!ok) return false;
        let saved = true;
        if (includeIncentive && limitsDirty && limitsRef.current) saved = await limitsRef.current.save();
        if (!settingsDirty) {
            if (saved) Message.success("Campaign saved");
            return saved;
        }
        return saved && saveCampaign({
            name: campaignName,
            type: campaignType,
            double_opt_in: doubleOptIn,
            include_incentive: includeIncentive,
            incentive_delivery: after.incentiveDelivery,
            form_thank_you_url: thankYouUrl,
            confirm_email_url: after.confirmedUrl,
            sender_email_id: uuidOrNull(senderEmailId),
            product_id: uuidOrNull(productId),
            contact_list_ids: selectedContactListIds,
        });
    };

    const discard = () => {
        setFromRecord(record);
        if (limitsRef.current) limitsRef.current.discard();
    };

    // Tell the campaign page the flow as it stands now.
    useEffect(() => {
        if (onFlowChange) {
            onFlowChange({
                doubleOptIn, includeIncentive, incentiveDelivery: after.incentiveDelivery, confirmedUrl: after.confirmedUrl,
                thankYouUrl, senderEmailId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doubleOptIn, includeIncentive, after.incentiveDelivery, after.confirmedUrl, thankYouUrl, senderEmailId]);
    if (controlRef) controlRef.current = { isDirty, save, discard };

    const deleteCampaign = () => {
        Modal.confirm({
            title: "Delete this campaign?",
            content: "This permanently deletes the campaign and its forms/emails. This can't be undone.",
            okText: "Delete",
            okButtonProps: { status: "danger" },
            onOk: () => {
                DeleteApi(getAccessTokenSilently, "/api/campaigns/" + record.id,
                    () => { Message.success("Campaign deleted"); navigate("/signups"); },
                    () => navigate("/login"),
                    () => Message.error("Failed to delete campaign"));
            },
        });
    };

    const handleSetSelectedSubscriptions = (contactLists) => {
        // ONLY set the ids and let the useEffect connect it to the underlying selectedContactLists
        setSelectedContactListIds(contactLists.map(item => item.id))
    }

    // A list just created via the inline "New list" button: add it to the options
    // (the POST returned the full record, so no refetch) and select it. The
    // ids -> objects useEffect above re-links it once the options include it.
    const handleContactListCreated = (created) => {
        if (!created || !created.id) return;
        setAllContactListItems && setAllContactListItems((prev) => [...(prev || []), created]);
        setSelectedContactListIds((ids) => (ids.includes(created.id) ? ids : [...ids, created.id]));
    }

    // What happens after they confirm. With a reward it's how they get it, so
    // it's asked in the reward panel ("How they get it"); without one, under
    // the confirmation checkbox. Single opt-in has no confirm step.
    const afterConfirmQuestion = doubleOptIn ? (
        <AfterConfirmQuestion
            withReward={includeIncentive}
            choice={afterConfirm}
            onChoice={(v) => { setAfterConfirm(v); setUrlMissing(false); }}
            url={confirmedUrl}
            onUrl={(v) => { setConfirmedUrl(v); setUrlMissing(false); }}
            urlMissing={urlMissing && !confirmedUrl.trim()}
        />
    ) : null;

    return (

        <Row>
            <Col span={24}>
                <Field>
                    <FieldLabel>Name</FieldLabel>
                    <Input
                        style={{ width: "100%" }}
                        value={campaignName}
                        onChange={(v) => setCampaignName(v)}
                    />
                </Field>
            </Col>
            {/* Campaign Type is hidden while nothing uses it (its value is kept as-is). */}
            {SHOW_CAMPAIGN_TYPE && (
                <Col span={24}>
                    <Field>
                        <FieldLabel>Type</FieldLabel>
                        <Input
                            style={{ width: "100%" }}
                            value={campaignType}
                            onChange={(v) => setCampaignType(v)}
                        />
                    </Field>
                </Col>
            )}
            <Col span={24}>

                <Field data-fix-field="thank_you_url">
                    <FieldLabel>Form Thank You URL</FieldLabel>
                    <Input
                        style={{ width: "100%" }}
                        value={thankYouUrl}
                        onChange={(v) => setThankYouUrl(v)}
                    />
                </Field>
            </Col>
            <Col span={24}>
                <div data-fix-field="double_opt_in" style={{ display: "inline-block" }}>
                    <Checkbox
                        checked={doubleOptIn}
                        onChange={(checked) => setDoubleOptIn(checked)}
                    >
                        Require Confirmation (Double Opt In)
                    </Checkbox>
                </div>
            </Col>

            {doubleOptIn && (
                <Col span={24}>
                    {includeIncentive ? (
                        <Text style={{ display: "block", margin: "4px 0 8px 24px", color: "var(--color-text-3)", fontSize: 12 }} data-fix-field="confirmed_url">
                            What happens after they click Confirm is set with the reward, below.
                        </Text>
                    ) : (
                        <Field style={{ marginLeft: 24 }} data-fix-field="confirmed_url">
                            {afterConfirmQuestion}
                        </Field>
                    )}
                </Col>
            )}

            <Col span={24}>

                <Checkbox
                    checked={includeIncentive}
                    onChange={toggleIncentive}
                >
                    Give signups a reward (a file or a link)
                </Checkbox>
            </Col>

            {/* The incentive itself (file or link, how it's delivered, limits,
                follow-up email) opens right under the option that enables it. The
                file/link and limits save on their own; the checkbox and delivery
                save with the settings. */}
            {includeIncentive && (
                <Col span={24}>
                    <IncentivePanel data-fix-field="reward">
                        {record && record.id ? (
                            <IncentiveEditor
                                campaignId={record.id}
                                campaign={{ double_opt_in: doubleOptIn, include_incentive: includeIncentive, incentive_delivery: after.incentiveDelivery }}
                                delivery={afterConfirmQuestion && <div data-fix-field="reward_delivery">{afterConfirmQuestion}</div>}
                                onIncentive={onIncentive}
                                onGoToEmails={onGoToEmails}
                                limitsRef={limitsRef}
                                onLimitsDirty={setLimitsDirty}
                            />
                        ) : afterConfirmQuestion && <div data-fix-field="reward_delivery">{afterConfirmQuestion}</div>}
                        {!(record && record.include_incentive) && (
                            <Text style={{ color: "rgb(var(--orange-6))", fontSize: 12 }}>
                                Save the campaign settings to start giving this to new signups.
                            </Text>
                        )}
                    </IncentivePanel>
                </Col>
            )}

            {
                confirmedSenderEmails.length > 0 && (
                    <Col span={24}>

                        <Field style={{ paddingBottom: "10px" }} data-fix-field="sender">
                            {/* Every campaign sends a signup email (single opt-in too), so
                                the sender is always needed, not just for double opt-in. */}
                            <FieldLabel>Send campaign emails from</FieldLabel>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Choose a verified sender"
                                status={hasSender ? undefined : "warning"}
                                value={hasSender ? senderEmailId : undefined}
                                onChange={(v) => { setSenderEmailId(v) }}
                            >

                                {
                                    confirmedSenderEmails.map((item, index) => {
                                        return (
                                            <Option key={item.id} value={item.id}>{item.name} ({item.email})</Option>
                                        )
                                    })
                                }
                            </Select>
                            {!hasSender && (
                                <Text style={{ color: "rgb(var(--orange-6))", fontSize: 12 }}>
                                    Required: without a sender, signup emails (and downloads) can't be sent.
                                </Text>
                            )}
                        </Field>
                    </Col>
                )
            }

            {
                confirmedSenderEmails.length === 0 && (
                    <Col span={24} data-fix-field="sender">

                        <Text style={{ color: "var(--color-text-3)" }}>No sender emails have been verified, <Link
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate("/settings")}
                        >check settings</Link></Text>
                    </Col>
                )

            }

            <Col span={24} data-fix-field="lists">

                <SubscriptionSelector
                    prompt={"Contact List Subscriptions to apply"}
                    allContactListItems={allContactListItems}
                    selectedSubscriptions={selectedContactLists}
                    setSelectedSubscriptions={handleSetSelectedSubscriptions}
                    onAddNew={() => setAddListOpen(true)}
                />
                <NewContactListDialog
                    open={addListOpen}
                    onClose={() => setAddListOpen(false)}
                    onCreated={(created) => { handleContactListCreated(created); setAddListOpen(false); }}
                />
            </Col>

            <Col span={24}>
                <Field>
                    <FieldLabel>Product (optional — the product this campaign promotes)</FieldLabel>
                    <Select
                        allowClear
                        style={{ width: "100%" }}
                        placeholder="No product"
                        value={productId || undefined}
                        onChange={(v) => setProductId(v || "")}
                    >
                        {products.map((p) => (
                            <Option key={p.id} value={p.id}>{p.name}</Option>
                        ))}
                    </Select>
                </Field>
            </Col>
            <Col span={24}>
                &nbsp;
            </Col>

            <Col span={24} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button type="primary" onClick={save} disabled={!isDirty}>
                    {SAVE_BUTTON_TEXT}
                </Button>
                {isDirty && (
                    <Button onClick={discard}>Discard</Button>
                )}
                <div style={{ flex: 1 }} />
                <Button type="secondary" status="danger" icon={<IconDelete />} onClick={deleteCampaign}>
                    {DELETE_BUTTON_TEXT}
                </Button>
            </Col>


        </Row>
    );
}
